import maplibregl from 'maplibre-gl'
import { addOverlayLayer } from '../../components/map/layer-order-manager.js'
import { isGeoJSONSource } from '../../components/map/map-engine.js'
import { utmZoneNumber, generateSubZoneGrid } from './utm-utils.js'
import { CACHED_GLYPHS } from '../tile-cache/vector-style-cache-service.js'

const SOURCE_ID = 'utm-grid-source'
const ZONE_LINE_LAYER = 'utm-zone-lines'
const ZONE_LABEL_LAYER = 'utm-zone-labels'
const GRID_LABEL_LAYER = 'utm-grid-line-labels'
const DEFAULT_OPACITY = 1.0

const ZONE_LETTERS = 'CDEFGHJKLMNPQRSTUVWX'
const BAND_BOTTOMS = Array.from({ length: 20 }, (_, i) => -80 + i * 8)

function subGridIntervals(zoom: number): number[] {
  const intervals: number[] = []
  if (zoom >= 5) intervals.push(100000)
  if (zoom >= 8) intervals.push(10000)
  if (zoom >= 11) intervals.push(1000)
  return intervals
}

function buildUtmGrid(bounds: maplibregl.LngLatBounds, zoom: number): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = []

  const west = Math.max(-180, bounds.getWest() - 6)
  const east = Math.min(180, bounds.getEast() + 6)
  const south = Math.max(-80, bounds.getSouth() - 8)
  const north = Math.min(84, bounds.getNorth() + 8)

  const startZone = Math.max(1, utmZoneNumber(bounds.getWest()))
  const endZone = Math.min(60, utmZoneNumber(bounds.getEast()))

  for (let z = startZone; z <= endZone + 1; z++) {
    const lng = (z - 1) * 6 - 180
    if (lng < west - 6 || lng > east + 6) continue
    features.push({
      type: 'Feature',
      properties: { type: 'zone-boundary' },
      geometry: {
        type: 'LineString',
        coordinates: [
          [lng, Math.max(-80, south)],
          [lng, Math.min(84, north)],
        ],
      },
    })
  }

  for (const bandLat of BAND_BOTTOMS) {
    if (bandLat < south - 8 || bandLat > north + 8) continue
    features.push({
      type: 'Feature',
      properties: { type: 'band-boundary' },
      geometry: {
        type: 'LineString',
        coordinates: [
          [Math.max(-180, west), bandLat],
          [Math.min(180, east), bandLat],
        ],
      },
    })
  }
  if (north >= 80) {
    features.push({
      type: 'Feature',
      properties: { type: 'band-boundary' },
      geometry: {
        type: 'LineString',
        coordinates: [
          [Math.max(-180, west), 84],
          [Math.min(180, east), 84],
        ],
      },
    })
  }

  if (zoom >= 3) {
    for (let z = startZone; z <= endZone; z++) {
      const zoneWest = (z - 1) * 6 - 180
      const zoneCenterLng = zoneWest + 3
      for (let bi = 0; bi < BAND_BOTTOMS.length; bi++) {
        const bandBot = BAND_BOTTOMS[bi]
        const bandTop = bi < BAND_BOTTOMS.length - 1 ? BAND_BOTTOMS[bi + 1] : 84
        if (bandBot > north || bandTop < south) continue
        features.push({
          type: 'Feature',
          properties: { type: 'zone-label', label: `${z}${ZONE_LETTERS[bi]}` },
          geometry: { type: 'Point', coordinates: [zoneCenterLng, (bandBot + bandTop) / 2] },
        })
      }
    }
  }

  const intervals = subGridIntervals(zoom)
  if (intervals.length > 0) {
    const viewSouth = Math.max(-80, bounds.getSouth())
    const viewNorth = Math.min(84, bounds.getNorth())
    const viewWest = bounds.getWest()
    const viewEast = bounds.getEast()

    for (let z = startZone; z <= endZone; z++) {
      const hemispheres: boolean[] = []
      if (viewNorth > 0) hemispheres.push(true)
      if (viewSouth < 0) hemispheres.push(false)

      for (const isNorth of hemispheres) {
        const hemiSouth = isNorth ? Math.max(0.01, viewSouth) : viewSouth
        const hemiNorth = isNorth ? viewNorth : Math.min(-0.01, viewNorth)
        if (hemiSouth >= hemiNorth) continue

        const clipBounds = {
          west: viewWest,
          east: viewEast,
          south: hemiSouth,
          north: hemiNorth,
        }

        for (const interval of intervals) {
          features.push(...generateSubZoneGrid(z, isNorth, clipBounds, interval, 'grid-line'))
        }
      }
    }
  }

  return { type: 'FeatureCollection', features }
}

class UtmGridService {
  private map: maplibregl.Map | null = null
  private _enabled = false
  private _opacity = DEFAULT_OPACITY
  private updateHandler = () => this.update()

  get enabled(): boolean {
    return this._enabled
  }
  get opacity(): number {
    return this._opacity
  }

  register(map: maplibregl.Map): void {
    this.map = map
  }

  unregister(): void {
    this.disable()
    this.map = null
  }

  enable(): void {
    if (!this.map || this._enabled) return
    this._enabled = true
    this.addSourceAndLayers()
    this.map.on('moveend', this.updateHandler)
    this.update()
  }

  disable(): void {
    if (!this.map) return
    this._enabled = false
    this.map.off('moveend', this.updateHandler)
    this.removeSourceAndLayers()
  }

  toggle(): void {
    if (this._enabled) this.disable()
    else this.enable()
  }

  setOpacity(opacity: number): void {
    this._opacity = Math.max(0, Math.min(1, opacity))
    if (!this.map || !(this.map as unknown as Record<string, unknown>).style) return
    if (this.map.getLayer(ZONE_LINE_LAYER)) {
      this.map.setPaintProperty(ZONE_LINE_LAYER, 'line-opacity', this._opacity)
    }
    if (this.map.getLayer(ZONE_LABEL_LAYER)) {
      this.map.setPaintProperty(ZONE_LABEL_LAYER, 'text-opacity', this._opacity)
    }
    if (this.map.getLayer(GRID_LABEL_LAYER)) {
      this.map.setPaintProperty(GRID_LABEL_LAYER, 'text-opacity', this._opacity)
    }
  }

  reAdd(): void {
    if (!this._enabled || !this.map) return
    this.removeSourceAndLayers()
    this.addSourceAndLayers()
    this.map.on('moveend', this.updateHandler)
    this.update()
  }

  private update(): void {
    if (!this.map || !this._enabled) return
    const bounds = this.map.getBounds()
    const zoom = this.map.getZoom()
    const data = buildUtmGrid(bounds, zoom)
    const gridLines = data.features.filter(f => f.properties?.type === 'grid-line')
    if (gridLines.length > 0) {
      const levels = new Set(gridLines.map(f => f.properties?.level))
      console.log(
        `[UTM] zoom=${zoom.toFixed(1)}, total=${data.features.length}, gridLines=${gridLines.length}, levels=${[...levels].join(',')}`
      )
    }
    const source = this.map.getSource(SOURCE_ID)
    if (isGeoJSONSource(source)) source.setData(data)
  }

  private addSourceAndLayers(): void {
    if (!this.map) return
    this.ensureGlyphs()

    if (!this.map.getSource(SOURCE_ID)) {
      this.map.addSource(SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
    }

    if (!this.map.getLayer(ZONE_LINE_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: ZONE_LINE_LAYER,
          type: 'line',
          source: SOURCE_ID,
          filter: ['!=', ['geometry-type'], 'Point'],
          paint: {
            'line-color': '#1565C0',
            'line-opacity': this._opacity,
            'line-width': [
              'match',
              ['get', 'type'],
              'zone-boundary',
              3,
              'band-boundary',
              2.5,
              ['match', ['get', 'level'], 100000, 2, 10000, 1.5, 1000, 1, 1.5],
            ],
          },
        },
        'utm-grid'
      )
    }

    if (!this.map.getLayer(ZONE_LABEL_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: ZONE_LABEL_LAYER,
          type: 'symbol',
          source: SOURCE_ID,
          filter: ['==', ['geometry-type'], 'Point'],
          layout: {
            'text-field': ['get', 'label'],
            'text-font': ['Noto Sans Bold'],
            'text-size': 14,
            'text-allow-overlap': true,
          },
          paint: {
            'text-color': '#1565C0',
            'text-opacity': this._opacity,
            'text-halo-color': 'rgba(255, 255, 255, 0.9)',
            'text-halo-width': 2,
          },
        },
        'utm-grid'
      )
    }

    if (!this.map.getLayer(GRID_LABEL_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: GRID_LABEL_LAYER,
          type: 'symbol',
          source: SOURCE_ID,
          filter: ['==', ['get', 'type'], 'grid-line'],
          layout: {
            'symbol-placement': 'line',
            'text-field': ['get', 'label'],
            'text-font': ['Noto Sans Regular'],
            'text-size': 11,
            'text-max-angle': 30,
            'text-padding': 40,
            'text-allow-overlap': false,
          },
          paint: {
            'text-color': '#1565C0',
            'text-opacity': this._opacity,
            'text-halo-color': 'rgba(255, 255, 255, 0.9)',
            'text-halo-width': 2,
          },
        },
        'utm-grid'
      )
    }
  }

  private ensureGlyphs(): void {
    if (!this.map) return
    const style = this.map.getStyle()
    if (!style.glyphs) {
      style.glyphs = CACHED_GLYPHS
      this.map.setStyle(style, { diff: true })
    }
  }

  private removeSourceAndLayers(): void {
    if (!this.map) return
    if (this.map.getLayer(GRID_LABEL_LAYER)) this.map.removeLayer(GRID_LABEL_LAYER)
    if (this.map.getLayer(ZONE_LABEL_LAYER)) this.map.removeLayer(ZONE_LABEL_LAYER)
    if (this.map.getLayer(ZONE_LINE_LAYER)) this.map.removeLayer(ZONE_LINE_LAYER)
    if (this.map.getSource(SOURCE_ID)) this.map.removeSource(SOURCE_ID)
  }
}

export const utmGridService = new UtmGridService()
